import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import EfesContentHeader from '../../components/EfesContentHeader';
import { EFES_CONTENT_WIDTH, EFES_SUMMARY_WIDTH, FULL_WIDTH } from '../../constants/uiConstants';
import useDeviceResize from '../../hooks/useDeviceResize';
import CalculatorSummary from '../../components/RoadCareCalculator/CalculatorSummary';
import { bindSummaryContractData } from '../../helpers/roadCare/bindData';
import { getContractSummary } from '../../helpers/roadCare/roadCareCalcHelper';
import RoadCareCalculator from '../../components/RoadCareCalculator';
import { getTransportUsageTypes } from '../../redux/modules/vehicles';
import { ACTION_NAME, APPLICATION_CONTRACT_TYPES, RISK_AMOUNT_TYPES } from '../../constants';
import {
  setReleaseYear,
  setTransportUsageType,
  getRiskAmounts,
  getContractCoverages,
  getPrices,
} from '../../redux/modules/roadCareCalculation';
import { ROAD_CARE_PLAN_TYPES } from '../../constants/roadCare';
import usePrevious from '../../hooks/usePrevious';
import {
  getCalculatorDiscount,
  getCalculatorTotalPrice,
  getCalculatorTotalPriceAfterDiscount,
} from '../../helpers/price';
import useSchedule from '../../hooks/useSchedule';
import useIsAutomaticRecurringPayment from '../../hooks/useIsAutomaticRecurringPayment';

import RoadCareCalcHeader from '../../img/roadCareCalcHeader.svg';
import summaryWrapperUseStyles from '../../components/MUI/SummaryWrapper/makeStyles';

const RoadCareCalculatorContainer = ({
  formProps,
  actionName,
  buyButtonHandler,
  roadCareDataSelector,
}) => {
  const { t } = useTranslation();
  const isDeviceLgSize = useDeviceResize('lg');
  const summaryWrapperClasses = summaryWrapperUseStyles();
  const dispatch = useDispatch();
  const roadCareDetails = useSelector(roadCareDataSelector, shallowEqual);

  const {
    releaseYear,
    prices,
    paymentFrequency,
    riskAmount,
    transportUsageType,
    paymentFrequencies,
    isFetchingPrice,
    range,
  } = roadCareDetails;
  const previousReleaseYear = usePrevious(releaseYear);
  const isAutomaticRecurringPayment = useIsAutomaticRecurringPayment(paymentFrequency);

  const [openMobileSummary, setOpenMobileSummary] = useState(false);

  const contractDetails = useMemo(
    () => bindSummaryContractData(t, { start: range.start, end: range.end }),
    [t, range],
  );

  const totalPrice = useMemo(() => getCalculatorTotalPrice(prices, paymentFrequency), [
    prices,
    paymentFrequency,
  ]);

  const totalDiscount = useMemo(() => getCalculatorDiscount(prices, paymentFrequency), [
    prices,
    paymentFrequency,
  ]);

  const totalPriceAfterDiscount = useMemo(
    () => getCalculatorTotalPriceAfterDiscount(prices, paymentFrequency),
    [prices, paymentFrequency],
  );

  const contractSummary = useMemo(
    () => getContractSummary(totalDiscount, totalPrice, t, isFetchingPrice),
    [isFetchingPrice, t, totalDiscount, totalPrice],
  );
  const calculatedSchedules = useSchedule(paymentFrequency.value, paymentFrequencies);

  useEffect(() => {
    dispatch(getTransportUsageTypes(APPLICATION_CONTRACT_TYPES.ROAD_CARE));
  }, [dispatch, actionName]);

  useEffect(() => {
    if (releaseYear && !previousReleaseYear) {
      dispatch(getRiskAmounts(actionName, RISK_AMOUNT_TYPES.AUTO_CARE));
      dispatch(getContractCoverages(
        actionName,
        APPLICATION_CONTRACT_TYPES.ROAD_CARE,
        ROAD_CARE_PLAN_TYPES.DEFAULT,
      ));
    }
  }, [releaseYear, actionName, dispatch, previousReleaseYear]);

  useEffect(() => {
    if (releaseYear && transportUsageType && riskAmount) {
      dispatch(getPrices({
        vehicles: [{
          vehicleDate: Number(releaseYear.value),
          riskAmount: Number(riskAmount),
          vehicleType: transportUsageType.key,
          passport: null,
        }],
      }, ACTION_NAME.CALCULATOR));
    }
  }, [dispatch, releaseYear, riskAmount, transportUsageType]);

  const dispatchReleaseYear = useCallback((selectedReleaseYear) => {
    dispatch(setReleaseYear(selectedReleaseYear, actionName));
  }, [actionName, dispatch]);

  const dispatchTransportUsageType = useCallback((selectedTransportUsageType) => {
    dispatch(setTransportUsageType(selectedTransportUsageType, actionName));
  }, [actionName, dispatch]);

  return (
    <>
      <EfesContentHeader
        headerText="roadCareCalculator.title"
        headerImage={RoadCareCalcHeader}
        imageWidth={50}
      />
      <Grid container>
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_CONTENT_WIDTH,
            paddingRight: isDeviceLgSize ? 0 : 16,
          }}
        >
          <RoadCareCalculator
            formProps={formProps}
            dispatchReleaseYear={dispatchReleaseYear}
            dispatchTransportUsageType={dispatchTransportUsageType}
            buyButtonHandler={buyButtonHandler}
            handleOpenMobileSummary={() => setOpenMobileSummary(!openMobileSummary)}
            isFetchingPrice={isFetchingPrice}
            totalPriceAfterDiscount={
              (isAutomaticRecurringPayment && calculatedSchedules.length)
                ? calculatedSchedules[0].amount : totalPriceAfterDiscount
            }
          />
        </Grid>
        <Grid
          item
          style={{
            width: isDeviceLgSize ? FULL_WIDTH : EFES_SUMMARY_WIDTH,
            paddingLeft: isDeviceLgSize ? 0 : 16,
          }}
        >
          <div className={summaryWrapperClasses.summaryWrapperBox}>
            <CalculatorSummary
              buyButtonHandler={buyButtonHandler}
              onDismiss={() => setOpenMobileSummary(!openMobileSummary)}
              openMobileSummary={openMobileSummary}
              contractDetails={contractDetails}
              contractSummary={contractSummary}
              isFetchingPrice={isFetchingPrice}
              totalPriceAfterDiscount={
                (isAutomaticRecurringPayment && calculatedSchedules.length)
                  ? calculatedSchedules[0].amount : totalPriceAfterDiscount
              }
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
};

RoadCareCalculatorContainer.propTypes = {
  roadCareDataSelector: PropTypes.func,
  formProps: PropTypes.shape({
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    errors: PropTypes.shape({}),
    clearError: PropTypes.func,
    watch: PropTypes.func,
  }).isRequired,
  actionName: PropTypes.string,
  buyButtonHandler: PropTypes.func.isRequired,
};

RoadCareCalculatorContainer.defaultProps = {
  actionName: ACTION_NAME.CALCULATOR,
  roadCareDataSelector: (state) => state.roadCareCalculation,
};

export default RoadCareCalculatorContainer;
